/**
 * Słońce dla projektu dachu 3D: czas warszawski, pozycja słońca, wschód i zachód,
 * natężenie przy bezchmurnym niebie, promieniowanie na płaszczyźnie i moc modułu.
 *
 * KONWENCJE (PLAN_PROJEKT_DACHU_3D.md, rozdz. 3): azymut w stopniach od północy zgodnie
 * z ruchem wskazówek zegara (N = 0, E = 90, S = 180, W = 270), liczony względem północy
 * geograficznej. Scena: x = wschód, y = góra, z = południe; do siatki EPSG:2180 przechodzimy
 * przez zbieżność południków: azymutSiatki = azymutPrawdziwy − zbieznosc.
 *
 * ŹRÓDŁA I DOKŁADNOŚĆ
 * - Pozycja słońca: algorytm NOAA (GML Solar Calculator, wzory J. Meeusa „Astronomical
 *   Algorithms”). Względem SPA NREL (Reda, Andreas 2008, ±0,0003°) różnica rzędu 0,01°;
 *   wysokość pozorna z refrakcją standardową NOAA (bez poprawki na ciśnienie).
 * - Wschód i zachód: środek tarczy na wysokości −0,833°, jak w NOAA; zgodne z tabelami NOAA
 *   (gml.noaa.gov/grad/solcalc/table.php) co do minuty. Południe słoneczne liczymy w chwili
 *   górowania; tabela NOAA bierze równanie czasu z początku doby i różni się o 15 s.
 * - Czas Europe/Warsaw liczony w kodzie, bez strefy przeglądarki: CET = UTC+1, CEST = UTC+2
 *   od ostatniej niedzieli marca 01:00 UTC do ostatniej niedzieli października 01:00 UTC
 *   (dyrektywa 2000/84/WE).
 * - Czyste niebo: Ineichen i Perez (2002) ze współczynnikiem zmętnienia Linkego (bez
 *   „poprawki Pereza”, jak domyślnie w pvlib), masa powietrza Kasten i Young (1989)
 *   z poprawką na ciśnienie, I0 = 1367 W/m² z poprawką odległości Spencera (1971).
 *   Domyślne TL = 3,5 to przybliżona średnia roczna dla nizin; latem zwykle więcej, zimą mniej.
 *   Grudniowe południe w Warszawie (słońce 14,4°) daje ok. 175 W/m² GHI, czerwcowe ok. 865.
 * - Płaszczyzna: model Hay i Davies (1980). Część rozproszona okołosłoneczna (udział
 *   DNI/I0) pada z kierunku słońca jak wiązka, reszta nieba izotropowo, grunt izotropowo.
 * - Moc modułu: temperatura ogniwa z NOCT (opcjonalna poprawka na wiatr wg Duffie
 *   i Beckman), liniowy współczynnik temperaturowy mocy; odbicia na szkle (IAM) wg ASHRAE.
 */

const RAD = Math.PI / 180
const DEG = 180 / Math.PI
const MS_GODZINY = 3600000
const MS_DOBY = 86400000

/** Stała słoneczna użyta przy wyznaczaniu współczynników modelu Ineichena i Pereza. */
export const STALA_SLONECZNA = 1367

const mod = (a, n) => ((a % n) + n) % n
const ms = (data) => (typeof data === 'number' ? data : data.getTime())

/* ── Kalendarz i czas warszawski ─────────────────────────────────────────── */

/** Numer dnia w roku, 1..366. `miesiac` 1–12. */
export const dzienRoku = (rok, miesiac, dzien) =>
  Math.round((Date.UTC(rok, miesiac - 1, dzien) - Date.UTC(rok, 0, 1)) / MS_DOBY) + 1

/** Dzień miesiąca ostatniej niedzieli w miesiącu o 31 dniach (marzec, październik). */
const ostatniaNiedziela = (rok, miesiac0) => 31 - new Date(Date.UTC(rok, miesiac0, 31)).getUTCDay()

/** Granice czasu letniego w danym roku jako chwile UTC (ms). */
export const graniceCzasuLetniego = (rok) => ({
  poczatek: Date.UTC(rok, 2, ostatniaNiedziela(rok, 2), 1),
  koniec: Date.UTC(rok, 9, ostatniaNiedziela(rok, 9), 1),
})

/** Czy w danej chwili (Date albo ms UTC) w Polsce obowiązuje czas letni. */
export const czasLetni = (data) => {
  const t = ms(data)
  const { poczatek, koniec } = graniceCzasuLetniego(new Date(t).getUTCFullYear())
  return t >= poczatek && t < koniec
}

/**
 * Chwila (Date, UTC) odpowiadająca czasowi lokalnemu Europe/Warsaw. `godzina` dziesiętnie,
 * może wykraczać poza 0–24 (przechodzi na sąsiedni dzień).
 * Godziny nieistniejące (przestawienie na czas letni, 02:00–03:00) przesuwamy o godzinę
 * do przodu; podwójne (powrót na zimowy) czytamy jako pierwsze wystąpienie, w czasie
 * letnim. Tak samo robią java.time i Temporal.
 */
export const czasWarszawy = (rok, miesiac, dzien, godzina = 0) => {
  const lokalnie = Date.UTC(rok, miesiac - 1, dzien) + godzina * MS_GODZINY
  const wLetnim = lokalnie - 2 * MS_GODZINY
  return new Date(czasLetni(wLetnim) ? wLetnim : lokalnie - MS_GODZINY)
}

/** Odwrotność `czasWarszawy`: data i godzina dziesiętna w Europe/Warsaw. */
export const godzinaWarszawy = (data) => {
  const t = ms(data)
  const d = new Date(t + (czasLetni(t) ? 2 : 1) * MS_GODZINY)
  return {
    rok: d.getUTCFullYear(),
    miesiac: d.getUTCMonth() + 1,
    dzien: d.getUTCDate(),
    godzina: d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600
      + d.getUTCMilliseconds() / MS_GODZINY,
  }
}

/* ── Pozycja słońca (NOAA) ──────────────────────────────────────────────── */

const wiekJulianski = (t) => (t / MS_DOBY + 2440587.5 - 2451545) / 36525

/** Deklinacja (°) i równanie czasu (min) dla wieku juliańskiego `t` (arkusz NOAA). */
const orbita = (t) => {
  const l0 = mod(280.46646 + t * (36000.76983 + t * 0.0003032), 360) * RAD
  const m = (357.52911 + t * (35999.05029 - 0.0001537 * t)) * RAD
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t)
  const srodek = Math.sin(m) * (1.914602 - t * (0.004817 + 0.000014 * t))
    + Math.sin(2 * m) * (0.019993 - 0.000101 * t) + Math.sin(3 * m) * 0.000289
  const omega = (125.04 - 1934.136 * t) * RAD
  const lambda = l0 + (srodek - 0.00569 - 0.00478 * Math.sin(omega)) * RAD
  const eps0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60
  const eps = (eps0 + 0.00256 * Math.cos(omega)) * RAD
  const y = Math.tan(eps / 2) ** 2
  return {
    deklinacja: Math.asin(Math.sin(eps) * Math.sin(lambda)) * DEG,
    rownanieCzasu: 4 * DEG * (y * Math.sin(2 * l0) - 2 * e * Math.sin(m)
      + 4 * e * y * Math.sin(m) * Math.cos(2 * l0) - 0.5 * y * y * Math.sin(4 * l0)
      - 1.25 * e * e * Math.sin(2 * m)),
  }
}

/** Poprawka refrakcji (°) dla wysokości geometrycznej `h` (°), wzór NOAA. */
const refrakcja = (h) => {
  if (h > 85) return 0
  const te = Math.tan(h * RAD)
  if (h > 5) return (58.1 / te - 0.07 / te ** 3 + 0.000086 / te ** 5) / 3600
  if (h > -0.575) return (1735 + h * (-518.2 + h * (103.4 + h * (-12.79 + h * 0.711)))) / 3600
  return -20.772 / te / 3600
}

/**
 * Pozycja słońca w chwili `data` (Date albo ms UTC) dla szerokości `lat` i długości `lon`
 * (stopnie, wschód dodatni). Zwraca azymut prawdziwy (°, od północy zgodnie z zegarem)
 * i wysokość pozorną nad horyzontem z refrakcją (°).
 */
export const pozycjaSlonca = (data, lat, lon) => {
  const t = ms(data)
  const { deklinacja, rownanieCzasu } = orbita(wiekJulianski(t))
  const minutyUtc = mod(t, MS_DOBY) / 60000
  const katGodzinny = (mod(minutyUtc + rownanieCzasu + 4 * lon, 1440) / 4 - 180) * RAD
  const fi = lat * RAD
  const delta = deklinacja * RAD
  const sinH = Math.sin(fi) * Math.sin(delta) + Math.cos(fi) * Math.cos(delta) * Math.cos(katGodzinny)
  const wysokosc = Math.asin(Math.max(-1, Math.min(1, sinH))) * DEG
  const azymut = mod(Math.atan2(Math.sin(katGodzinny),
    Math.cos(katGodzinny) * Math.sin(fi) - Math.tan(delta) * Math.cos(fi)) * DEG + 180, 360)
  return { azymut, wysokosc: wysokosc + refrakcja(wysokosc) }
}

/**
 * Wschód, zachód i południe słoneczne w lokalnym czasie warszawskim (godziny dziesiętne).
 * `wschod`/`zachod` = null, gdy słońce tego dnia nie wschodzi albo nie zachodzi.
 * Jak NOAA: deklinację i równanie czasu przeliczamy w chwili zdarzenia (kilka przybliżeń).
 */
export const wschodZachod = (rok, miesiac, dzien, lat, lon) => {
  const polnoc = Date.UTC(rok, miesiac - 1, dzien)
  const naGodzine = (minutyUtc) => {
    const w = godzinaWarszawy(polnoc + minutyUtc * 60000)
    const przesuniecieDni = Math.round((Date.UTC(w.rok, w.miesiac - 1, w.dzien) - polnoc) / MS_DOBY)
    return w.godzina + 24 * przesuniecieDni
  }
  let poludnie = 720 - 4 * lon
  for (let i = 0; i < 3; i += 1) {
    poludnie = 720 - 4 * lon - orbita(wiekJulianski(polnoc + poludnie * 60000)).rownanieCzasu
  }
  const fi = lat * RAD
  const zdarzenie = (znak) => {
    let minuty = poludnie
    for (let i = 0; i < 4; i += 1) {
      const { deklinacja, rownanieCzasu } = orbita(wiekJulianski(polnoc + minuty * 60000))
      const delta = deklinacja * RAD
      const cosH = Math.cos(90.833 * RAD) / (Math.cos(fi) * Math.cos(delta)) - Math.tan(fi) * Math.tan(delta)
      if (cosH > 1 || cosH < -1) return null
      minuty = 720 + znak * 4 * Math.acos(cosH) * DEG - 4 * lon - rownanieCzasu
    }
    return naGodzine(minuty)
  }
  return { wschod: zdarzenie(-1), zachod: zdarzenie(1), poludnie: naGodzine(poludnie) }
}

/**
 * Wektor jednostkowy od sceny DO słońca w układzie sceny (x = wschód, y = góra, z = południe).
 * `zbieznosc` (°) przenosi azymut prawdziwy na azymut siatki EPSG:2180.
 */
export const wektorSlonca = (azymut, wysokosc, zbieznosc = 0) => {
  const ag = (azymut - zbieznosc) * RAD
  const h = wysokosc * RAD
  return [Math.sin(ag) * Math.cos(h), Math.sin(h), -Math.cos(ag) * Math.cos(h)]
}

/* ── Promieniowanie ─────────────────────────────────────────────────────── */

/** Natężenie pozaziemskie na powierzchnię prostopadłą (W/m²) w dniu roku `dzien`. */
export const natezeniePozaziemskie = (dzien) => {
  const g = (2 * Math.PI * (dzien - 1)) / 365
  return STALA_SLONECZNA * (1.00011 + 0.034221 * Math.cos(g) + 0.00128 * Math.sin(g)
    + 0.000719 * Math.cos(2 * g) + 0.000077 * Math.sin(2 * g))
}

/** Względna masa powietrza, Kasten i Young (1989); `wysokosc` pozorna w stopniach. */
export const masaPowietrza = (wysokosc) => {
  const z = 90 - wysokosc
  if (z >= 90) return Infinity
  return 1 / (Math.cos(z * RAD) + 0.50572 * (96.07995 - z) ** -1.6364)
}

/**
 * Natężenie przy bezchmurnym niebie (W/m²), model Ineichena i Pereza (2002).
 * `wysokosc` pozorna (°), `dzien` = dzień roku. Zwraca też `i0` (natężenie pozaziemskie),
 * potrzebne modelowi Haya i Daviesa. Pod horyzontem same zera.
 */
export const czysteNiebo = (wysokosc, dzien, { linke = 3.5, wysokoscNpm = 150 } = {}) => {
  const i0 = natezeniePozaziemskie(dzien)
  if (!(wysokosc > 0)) return { dni: 0, dhi: 0, ghi: 0, i0 }
  const cosZ = Math.sin(wysokosc * RAD)
  const am = masaPowietrza(wysokosc) * Math.exp(-wysokoscNpm / 8434.5)
  const fh1 = Math.exp(-wysokoscNpm / 8000)
  const fh2 = Math.exp(-wysokoscNpm / 1250)
  const cg1 = 5.09e-5 * wysokoscNpm + 0.868
  const cg2 = 3.92e-5 * wysokoscNpm + 0.0387
  const ghi = cg1 * i0 * cosZ * Math.exp(-cg2 * am * (fh1 + fh2 * (linke - 1)))
  const dniModel = (0.664 + 0.163 / fh1) * i0 * Math.exp(-0.09 * am * (linke - 1))
  const dniGorne = (ghi * (1 - (0.1 - 0.2 * Math.exp(-linke)) / (0.1 + 0.882 / fh1))) / cosZ
  const dni = Math.max(0, Math.min(dniModel, dniGorne))
  return { dni, dhi: Math.max(0, ghi - dni * cosZ), ghi, i0 }
}

/** Cosinus kąta padania promieni na płaszczyznę (ujemny: słońce za płaszczyzną). */
export const cosinusPadania = (slonce, plaszczyzna) => {
  const h = slonce.wysokosc * RAD
  const beta = plaszczyzna.nachylenie * RAD
  return Math.sin(h) * Math.cos(beta)
    + Math.cos(h) * Math.sin(beta) * Math.cos((slonce.azymut - plaszczyzna.azymut) * RAD)
}

/**
 * Promieniowanie na płaszczyźnie (W/m²), model Haya i Daviesa. `niebo` = wynik `czysteNiebo`
 * (albo pomiar {dni, dhi, ghi, i0?}), `plaszczyzna` = {nachylenie, azymut} prawdziwy.
 * `rozproszone` obejmuje część okołosłoneczną, podaną też osobno jako `okoloSloneczne`:
 * przy liczeniu cienia zasłania się ją razem z wiązką.
 */
export const naPlaszczyznie = (niebo, slonce, plaszczyzna, albedo = 0.2) => {
  const { dni = 0, dhi = 0, ghi = 0 } = niebo
  const i0 = niebo.i0 || STALA_SLONECZNA
  const cosTheta = cosinusPadania(slonce, plaszczyzna)
  const cosBeta = Math.cos(plaszczyzna.nachylenie * RAD)
  const ponadHoryzontem = slonce.wysokosc > 0
  const wiazka = ponadHoryzontem ? dni * Math.max(0, cosTheta) : 0
  const rb = ponadHoryzontem ? Math.max(0, cosTheta) / Math.max(Math.sin(slonce.wysokosc * RAD), 0.01745) : 0
  const anizotropia = Math.min(1, Math.max(0, dni / i0))
  const okoloSloneczne = dhi * anizotropia * rb
  const rozproszone = okoloSloneczne + dhi * (1 - anizotropia) * ((1 + cosBeta) / 2)
  const odbite = ghi * albedo * ((1 - cosBeta) / 2)
  return { poa: wiazka + rozproszone + odbite, wiazka, rozproszone, odbite, cosTheta, okoloSloneczne }
}

/* ── Moduł ──────────────────────────────────────────────────────────────── */

/**
 * Współczynnik kąta padania (odbicia na szkle), model ASHRAE: 1 − b0·(1/cosθ − 1).
 * b0 = 0,05 to typowa wartość dla szkła modułu; przy θ ≈ 87° spada do zera.
 */
export const wspolczynnikPadania = (cosTheta, b0 = 0.05) =>
  (cosTheta <= 0 ? 0 : Math.max(0, Math.min(1, 1 - b0 * (1 / cosTheta - 1))))

/**
 * Temperatura ogniwa (°C) z NOCT: Ta + (NOCT − 20)·G/800. `wiatr` (m/s przy module)
 * skaluje przyrost czynnikiem 9,5/(5,7 + 3,8·v) (Duffie i Beckman; przy 1 m/s równy 1).
 */
export const temperaturaOgniwa = ({ poa, tempOtoczenia = 20, noct = 44, wiatr }) => {
  let przyrost = ((noct - 20) / 800) * Math.max(0, poa)
  if (Number.isFinite(wiatr)) przyrost *= 9.5 / (5.7 + 3.8 * Math.max(0, wiatr))
  return tempOtoczenia + przyrost
}

/** Moc DC modułu (W) przy natężeniu `poa` (W/m²); `pmax` = moc w STC, `gamma` w 1/K. */
export const mocModulu = ({ poa, tempOtoczenia = 20, pmax, gamma = -0.0035, noct = 44, wiatr }) => {
  if (!(poa > 0) || !(pmax > 0)) return 0
  const tc = temperaturaOgniwa({ poa, tempOtoczenia, noct, wiatr })
  return Math.max(0, pmax * (poa / 1000) * (1 + gamma * (tc - 25)))
}
